export type ChronologyMode = 'standard' | 'extended';
export type ChronologyValidationStatus = 'verified_range' | 'experimental' | 'unavailable';
export type ChronologyCalendar = 'solar' | 'lunar';

export interface AstronomicalDateParts {
  /** Astronomical year numbering: 1 = 1 CE, 0 = 1 BCE, -1 = 2 BCE. */
  year: number;
  month: number;
  day: number;
}

export interface ChronologyRequest {
  calendar: ChronologyCalendar;
  date: AstronomicalDateParts;
}

export interface ChronologyProviderDescriptor {
  id: string;
  version: string;
  mode: ChronologyMode;
  validationStatus: Exclude<ChronologyValidationStatus, 'unavailable'>;
  validatedYearRange?: Readonly<{ min: number; max: number }>;
  disclosure: string;
}

export interface ChronologyResolution {
  mode: ChronologyMode;
  providerId: string;
  providerVersion: string;
  validationStatus: ChronologyValidationStatus;
  inValidatedRange: boolean;
  request: ChronologyRequest;
  disclosure: string;
}

export interface ChronologyProvider {
  descriptor: ChronologyProviderDescriptor;
  resolve(request: ChronologyRequest): ChronologyResolution;
}

export interface ChronologyRouterOptions {
  standard: ChronologyProvider;
  extended?: ChronologyProvider;
}

export interface ChronologyModeAvailability {
  mode: ChronologyMode;
  available: boolean;
  providerId?: string;
  providerVersion?: string;
  validationStatus: ChronologyValidationStatus;
  disclosure: string;
}

export interface ChronologySelection {
  mode: ChronologyMode;
  experimentalAcknowledged: boolean;
}

export interface ChronologyModeOptionViewModel extends ChronologyModeAvailability {
  label: string;
  badge: '正式' | '研究' | '未啟用';
  selectable: boolean;
}

export interface ChronologySettingsViewModel {
  selectedMode: ChronologyMode;
  options: readonly ChronologyModeOptionViewModel[];
  requiresExperimentalAcknowledgement: boolean;
  canCalculate: boolean;
  disclosure: string;
}

function assertDateParts(date: AstronomicalDateParts): void {
  if (!Number.isSafeInteger(date.year)) throw new Error('Chronology year must be a safe integer using astronomical year numbering');
  if (!Number.isInteger(date.month) || date.month < 1 || date.month > 12) throw new Error('Chronology month must be 1..12');
  if (!Number.isInteger(date.day) || date.day < 1 || date.day > 31) throw new Error('Chronology day must be 1..31 before provider-specific calendar validation');
}

export function parseAstronomicalDate(value: string): AstronomicalDateParts {
  const match = /^([+-]?\d+)-(\d{1,2})-(\d{1,2})$/.exec(value.trim());
  if (!match) throw new Error('Astronomical date format must be signed-year-M-D');
  const date = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) };
  assertDateParts(date);
  return Object.freeze(date);
}

export function formatAstronomicalYear(year: number): string {
  if (!Number.isSafeInteger(year)) throw new Error('Astronomical year must be a safe integer');
  if (year > 0) return `公元 ${year} 年`;
  return `公元前 ${1 - year} 年`;
}

function inRange(year: number, range?: Readonly<{ min: number; max: number }>): boolean {
  return range ? year >= range.min && year <= range.max : false;
}

export class PassthroughStandardChronologyProvider implements ChronologyProvider {
  readonly descriptor: ChronologyProviderDescriptor;

  constructor(validatedYearRange: Readonly<{ min: number; max: number }>) {
    if (!Number.isSafeInteger(validatedYearRange.min) || !Number.isSafeInteger(validatedYearRange.max) || validatedYearRange.min > validatedYearRange.max) {
      throw new Error('Standard chronology validated year range is invalid');
    }
    this.descriptor = Object.freeze({
      id: 'STANDARD-IZTRO-COMPAT',
      version: '0.1.0',
      mode: 'standard',
      validationStatus: 'verified_range',
      validatedYearRange: Object.freeze({ ...validatedYearRange }),
      disclosure: 'Standard Chronology 只代表目前正式 Regression 承諾範圍；實際公農曆與紫微排盤仍由已鎖版的 calculation adapter 驗證。',
    });
  }

  resolve(request: ChronologyRequest): ChronologyResolution {
    assertDateParts(request.date);
    const inside = inRange(request.date.year, this.descriptor.validatedYearRange);
    if (!inside) throw new Error(`Standard Chronology rejected year ${request.date.year} outside verified range`);
    return Object.freeze({
      mode: 'standard',
      providerId: this.descriptor.id,
      providerVersion: this.descriptor.version,
      validationStatus: 'verified_range',
      inValidatedRange: true,
      request: Object.freeze({ ...request, date: Object.freeze({ ...request.date }) }),
      disclosure: this.descriptor.disclosure,
    });
  }
}

export class ChronologyRouter {
  readonly #standard: ChronologyProvider;
  readonly #extended: ChronologyProvider | undefined;

  constructor(options: ChronologyRouterOptions) {
    if (options.standard.descriptor.mode !== 'standard') throw new Error('Chronology router standard provider must declare mode=standard');
    if (options.extended && options.extended.descriptor.mode !== 'extended') throw new Error('Chronology router extended provider must declare mode=extended');
    this.#standard = options.standard;
    this.#extended = options.extended;
  }

  availability(mode: ChronologyMode): ChronologyModeAvailability {
    const provider = mode === 'standard' ? this.#standard : this.#extended;
    if (!provider) {
      return Object.freeze({
        mode,
        available: false,
        validationStatus: 'unavailable',
        disclosure: 'Extended Chronology 介面已預留，但目前未註冊 Provider；系統不會自動 fallback 或假裝支援長年代。',
      });
    }
    return Object.freeze({
      mode,
      available: true,
      providerId: provider.descriptor.id,
      providerVersion: provider.descriptor.version,
      validationStatus: provider.descriptor.validationStatus,
      disclosure: provider.descriptor.disclosure,
    });
  }

  resolve(mode: ChronologyMode, request: ChronologyRequest): ChronologyResolution {
    const provider = mode === 'standard' ? this.#standard : this.#extended;
    if (!provider) throw new Error(`Chronology mode ${mode} is unavailable because no provider is registered`);
    const result = provider.resolve(request);
    if (result.mode !== mode || provider.descriptor.mode !== mode) throw new Error(`Chronology provider mode mismatch for ${mode}`);
    if (result.providerId !== provider.descriptor.id || result.providerVersion !== provider.descriptor.version) {
      throw new Error(`Chronology provider identity drift for ${mode}`);
    }
    return result;
  }
}

export function buildChronologySettingsViewModel(
  router: ChronologyRouter,
  selection: ChronologySelection,
): ChronologySettingsViewModel {
  const options = (['standard', 'extended'] as const).map((mode): ChronologyModeOptionViewModel => {
    const availability = router.availability(mode);
    const badge = !availability.available ? '未啟用' : availability.validationStatus === 'experimental' ? '研究' : '正式';
    return Object.freeze({
      ...availability,
      label: mode === 'standard' ? 'Standard Chronology' : 'Extended Chronology',
      badge,
      selectable: availability.available,
    });
  });
  const selected = options.find((option) => option.mode === selection.mode)!;
  const requiresExperimentalAcknowledgement = selected.validationStatus === 'experimental';
  const canCalculate = selected.available && (!requiresExperimentalAcknowledgement || selection.experimentalAcknowledged);
  return Object.freeze({
    selectedMode: selection.mode,
    options: Object.freeze(options),
    requiresExperimentalAcknowledgement,
    canCalculate,
    disclosure: selection.mode === 'standard'
      ? '正式模式只使用已驗證範圍與對應 Provider。'
      : 'Extended Chronology 是進階研究模式；未完成曆法與天文 Regression 前不視為正式排盤能力。',
  });
}

export function assertChronologySelectionCanCalculate(router: ChronologyRouter, selection: ChronologySelection): void {
  const view = buildChronologySettingsViewModel(router, selection);
  if (!view.canCalculate) {
    if (!router.availability(selection.mode).available) throw new Error(`Chronology mode ${selection.mode} is not enabled`);
    throw new Error('Extended Chronology requires explicit experimental acknowledgement before calculation');
  }
}

export function createExperimentalExtendedChronologyStub(id = 'EXTENDED-CHRONOLOGY-RESERVED', version = '0.1.0'): ChronologyProvider {
  return Object.freeze({
    descriptor: Object.freeze({
      id,
      version,
      mode: 'extended' as const,
      validationStatus: 'experimental' as const,
      disclosure: 'Extended Chronology 為研究／特殊用途模式；目前只驗證可切換介面，不代表朔望、節氣、歷史曆法、ΔT 或時區模型已完成。',
    }),
    resolve(request: ChronologyRequest): ChronologyResolution {
      assertDateParts(request.date);
      return Object.freeze({
        mode: 'extended',
        providerId: id,
        providerVersion: version,
        validationStatus: 'experimental',
        inValidatedRange: false,
        request: Object.freeze({ ...request, date: Object.freeze({ ...request.date }) }),
        disclosure: 'Extended Chronology 目前僅為架構切換驗證；不得將此結果視為正式長年代曆法計算。',
      });
    },
  });
}
